import React, { useState } from "react";
import { Button, message, Select } from "antd";
import { ShoppingCartOutlined } from "@ant-design/icons";
import { useKeyboardShortcuts, useScrollIntoView } from "../../../Hook";
import { SHORTCUTS } from "../../../Constant";
import { useCart } from "../../../Context";
import { useSectionAccess } from "../../../Services";
import { DiscountMode } from "../../../Enums";
import { PosAction } from "../../../Types";
import { Banknote, PanelRight, ReceiptText, Undo2 } from "lucide-react";

interface CartItemsProps {
  setSelectedProduct: (product: any) => void;
  setSelectedBatch: (batch: any) => void;
  setCustomPrice: (price: number) => void;
  setCustomQty: React.Dispatch<React.SetStateAction<number>>;
  setCustomCost: (cost: number) => void;
  setDescription: (desc: string) => void;
  setCustomDiscount: (discount: number) => void;
  setDiscountMode: (mode: DiscountMode) => void;
  onToggleDrawer: () => void;
  onOpenToPay: () => void;
  onOpenReturn: () => void;
  onOpenExpenses: () => void;
  customers: any[];
  tables: any[];
  loadingCustomers: boolean;
  loadingTables: boolean;
  isRestaurant: boolean;
}

export const CartItems: React.FC<CartItemsProps> = ({
  setSelectedProduct,
  setSelectedBatch,
  setCustomPrice,
  setCustomQty,
  setCustomCost,
  setDescription,
  setCustomDiscount,
  setDiscountMode,
  onToggleDrawer,
  onOpenToPay,
  onOpenReturn,
  onOpenExpenses,
  customers,
  tables,
  loadingCustomers,
  loadingTables,
  isRestaurant,
}) => {
  const {
    cart,
    removeFromCart,
    cartTotal,
    customer,
    setCustomer,
    table,
    setTable,
    exchangeDiscount,
    setExchangeDiscount,
    quotationTempCustomer,
    setQuotationTempCustomer,
  } = useCart();
  const [focusedCartItemIndex, setFocusedCartItemIndex] = useState<
    number | null
  >(null);

  useScrollIntoView(
    focusedCartItemIndex,
    ".cart-items-container",
    ".cart-item-highlighted",
  );

  const { isAllowed } = useSectionAccess("pos");

  const activateCartNavigation = () => {
    if (cart.length > 0) {
      setFocusedCartItemIndex(0);
    } else {
      message.warning("Cart is empty");
    }
  };

  const removeFocusedCartItem = () => {
    if (focusedCartItemIndex === null) return;

    const itemToDelete = cart[focusedCartItemIndex];
    if (!itemToDelete) return;

    if (itemToDelete.rawProduct?.is_repair_item) {
      message.warning("Repair items in cart cannot be edited or deleted.");
      return;
    }

    removeFromCart(itemToDelete.id, itemToDelete.batch_no);
    setFocusedCartItemIndex((prev) => {
      if (cart.length <= 1) return null;
      if (prev !== null && prev >= cart.length - 1) return cart.length - 2;
      return prev;
    });
  };

  const editFocusedCartItem = () => {
    if (focusedCartItemIndex === null) return;

    const itemToEdit = cart[focusedCartItemIndex];
    if (!itemToEdit) return;

    if (itemToEdit.rawProduct?.is_repair_item) {
      message.warning("Repair items in cart cannot be edited or deleted.");
      return;
    }

    let rawProduct = itemToEdit.rawProduct;
    if (!rawProduct) {
      rawProduct = {
        id: itemToEdit.id,
        title: itemToEdit.name,
        image_paths: itemToEdit.image || "",
        is_tempory_item: itemToEdit.is_tempory_item ? true : undefined,
        stocks: [
          {
            id: 0,
            batch_no: itemToEdit.batch_no,
            retail_price: itemToEdit.price,
            cost_price: itemToEdit.cost_price || 0,
            wholesale_price: 0,
            quantity: itemToEdit.qty,
          },
        ],
      };
    }

    if (itemToEdit.is_tempory_item && !rawProduct.is_tempory_item) {
      rawProduct = { ...rawProduct, is_tempory_item: true };
    }

    if (!rawProduct.id) {
      rawProduct = { ...rawProduct, id: itemToEdit.id };
    }

    setSelectedProduct(rawProduct);
    const batch =
      rawProduct.stocks?.find((s: any) => s.batch_no === itemToEdit.batch_no) ||
      null;
    setSelectedBatch(batch);
    setCustomPrice(itemToEdit.price);
    setCustomQty(itemToEdit.qty);
    if (itemToEdit?.is_tempory_item) {
      setCustomCost(itemToEdit.cost_price || 0);
    }
    if (itemToEdit?.is_tempory_item) {
      setSelectedProduct({ ...rawProduct, title: itemToEdit.name });
    }
    setDescription(itemToEdit.description || "");
    setCustomDiscount(itemToEdit.discount_input ?? 0);
    if (itemToEdit.discount_mode) {
      setDiscountMode(itemToEdit.discount_mode);
    }
    removeFromCart(itemToEdit.id, itemToEdit.batch_no);
    setFocusedCartItemIndex(null);
  };

  useKeyboardShortcuts(
    [
      {
        shortcut: SHORTCUTS.POS.SELECT_CART,
        handler: () => {
          activateCartNavigation();
        },
        options: { enabled: cart.length > 0 },
      },
      {
        shortcut: SHORTCUTS.POS.CART_NEXT,
        options: { enabled: focusedCartItemIndex !== null },
        handler: () => {
          if (cart.length === 0) {
            setFocusedCartItemIndex(null);
            return;
          }
          setFocusedCartItemIndex((prev) =>
            prev === null ? 0 : (prev + 1) % cart.length,
          );
        },
      },
      {
        shortcut: SHORTCUTS.POS.CART_PREVIOUS,
        options: { enabled: focusedCartItemIndex !== null },
        handler: () => {
          if (cart.length === 0) {
            setFocusedCartItemIndex(null);
            return;
          }
          setFocusedCartItemIndex((prev) =>
            prev === null ? 0 : (prev - 1 + cart.length) % cart.length,
          );
        },
      },
      {
        shortcut: SHORTCUTS.POS.CART_REMOVE,
        options: { enabled: focusedCartItemIndex !== null },
        handler: () => {
          removeFocusedCartItem();
        },
      },
      {
        shortcut: SHORTCUTS.POS.CART_REMOVE_BACKSPACE,
        options: { enabled: focusedCartItemIndex !== null },
        handler: () => {
          removeFocusedCartItem();
        },
      },
      {
        shortcut: SHORTCUTS.POS.CART_EDIT,
        options: { enabled: focusedCartItemIndex !== null },
        handler: () => {
          editFocusedCartItem();
        },
      },
      {
        shortcut: SHORTCUTS.POS.CART_CLOSE,
        options: { enabled: focusedCartItemIndex !== null },
        handler: () => {
          setFocusedCartItemIndex(null);
        },
      },
      {
        shortcut: SHORTCUTS.POS.TOGGLE_DRAWER,
        options: {
          enabled: focusedCartItemIndex === null,
          enableOnFormTags: false,
          enableOnContentEditable: false,
        },
        handler: (e) => {
          e.preventDefault();
          e.stopPropagation();
          onToggleDrawer();
        },
      },
      {
        shortcut: SHORTCUTS.POS.PAY_CASH,
        options: { enabled: cart.length > 0 },
        handler: (e) => {
          e.preventDefault();
          e.stopPropagation();
          onOpenToPay();
        },
      },
      {
        shortcut: SHORTCUTS.POS.RETURN_DRAWER,
        options: {
          enabled: true,
          enableOnFormTags: false,
          enableOnContentEditable: false,
        },
        handler: (e) => {
          e.preventDefault();
          e.stopPropagation();
          onOpenReturn();
        },
      },
      {
        shortcut: SHORTCUTS.POS.EXPENSE_DRAWER,
        options: {
          enabled: true,
          enableOnFormTags: false,
          enableOnContentEditable: false,
        },
        handler: (e) => {
          e.preventDefault();
          e.stopPropagation();
          onOpenExpenses();
        },
      },
    ],
    { capture: true, stopImmediatePropagation: true },
  );

  return (
    <>
      <section className="bg-white rounded-2xl border border-gray-200/80 shadow-sm py-5 px-3 flex flex-col justify-between min-h-[500px]">
        <div className="flex flex-col gap-5 overflow-y-auto max-h-[480px] cart-items-container">
          <div className="flex items-center justify-between border-b border-gray-100 pb-4">
            <div className="flex items-center gap-x-2 flex-grow mr-2">
              <Select
                allowClear
                showSearch
                placeholder="Select Customer"
                className="flex-1 min-w-0"
                size="middle"
                value={customer?.id || undefined}
                onChange={(val) => {
                  const selected = customers.find((c: any) => c.id === val);
                  if (selected) {
                    setCustomer({
                      id: selected.id,
                      name: selected.name,
                      credit_limit: selected?.credit_limit,
                    });
                    setQuotationTempCustomer(null);
                  } else {
                    setCustomer(null);
                  }
                }}
                loading={loadingCustomers}
                optionFilterProp="label"
                options={customers.map((c: any) => ({
                  value: c.id,
                  label: `${c.name} (${c.phone})`,
                }))}
              />

              {isRestaurant && (
                <Select
                  allowClear
                  showSearch
                  placeholder="Select Table"
                  className="flex-1 min-w-0"
                  size="middle"
                  value={table?.id || undefined}
                  onChange={(val) => {
                    const selected = tables.find((t: any) => t.id === val);
                    if (selected) {
                      setTable({ id: selected.id, name: selected.name });
                    } else {
                      setTable(null);
                    }
                  }}
                  loading={loadingTables}
                  optionFilterProp="label"
                  options={tables
                    .filter(
                      (t: any) =>
                        t.current_status === "available" || t.id === table?.id,
                    )
                    .map((t: any) => ({
                      value: t.id,
                      label: t.name,
                    }))}
                />
              )}
            </div>
            <Button size="small" onClick={onToggleDrawer}>
              <PanelRight className="w-4 h-4 text-gray-500 hover:text-indigo-600 transition-colors" />
            </Button>
          </div>

          {/* Temporary Customer details if editing/loading a quotation with temporary customer */}
          {!customer && quotationTempCustomer && (
            <div className="bg-indigo-50 border border-indigo-100 text-indigo-900 rounded-xl p-3 flex flex-col gap-1 relative shadow-sm mx-1">
              <div className="flex justify-between items-center pr-4">
                <span className="text-[9px] font-black text-indigo-500 uppercase tracking-widest">
                  Temporary Customer
                </span>
                <Button
                  type="text"
                  size="small"
                  className="absolute top-1.5 right-1.5 text-indigo-400 hover:text-indigo-600 h-5 w-5 p-0 flex items-center justify-center text-xs"
                  onClick={() => setQuotationTempCustomer(null)}
                >
                  ✕
                </Button>
              </div>
              <div className="text-xs font-bold text-indigo-950 mt-0.5">
                {quotationTempCustomer.name}
              </div>
              {quotationTempCustomer.phone && (
                <div className="text-[10px] text-gray-500">
                  Phone: {quotationTempCustomer.phone}
                </div>
              )}
              {quotationTempCustomer.email && (
                <div className="text-[10px] text-gray-500">
                  Email: {quotationTempCustomer.email}
                </div>
              )}
              {quotationTempCustomer.address && (
                <div className="text-[10px] text-gray-400 truncate">
                  Addr: {quotationTempCustomer.address}
                </div>
              )}
            </div>
          )}

          {/* Cart Items list */}
          {cart.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-16 h-16 rounded-full bg-gray-50 flex items-center justify-center text-gray-400 mb-4">
                <ShoppingCartOutlined style={{ fontSize: "24px" }} />
              </div>
              <h3 className="font-semibold text-gray-800 text-sm">
                Cart is empty
              </h3>
              <p className="text-xs text-gray-400 mt-1 max-w-[180px]">
                Add products from the grid to begin checkout
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {cart.map((item, index) => {
                const isHighlighted = focusedCartItemIndex === index;
                return (
                  <div
                    key={`${item.id}-${item.batch_no}`}
                    onClick={() => {
                      if (item.rawProduct?.is_repair_item) return;
                      let rawProduct = item.rawProduct;
                      if (!rawProduct) {
                        rawProduct = {
                          id: item.id,
                          title: item.name,
                          image_paths: item.image || "",
                          is_tempory_item: item.is_tempory_item
                            ? true
                            : undefined,
                          stocks: [
                            {
                              id: 0,
                              batch_no: item.batch_no,
                              retail_price: item.price,
                              cost_price: item.cost_price || 0,
                              wholesale_price: 0,
                              quantity: item.qty,
                            },
                          ],
                        };
                      }
                      if (item.is_tempory_item && !rawProduct.is_tempory_item) {
                        rawProduct = { ...rawProduct, is_tempory_item: true };
                      }
                      if (item.is_tempory_item) {
                        rawProduct = { ...rawProduct, title: item.name };
                      }
                      if (!rawProduct.id) {
                        rawProduct = { ...rawProduct, id: item.id };
                      }
                      const batch =
                        rawProduct.stocks?.find(
                          (s: any) => s.batch_no === item.batch_no,
                        ) || null;
                      setSelectedProduct(rawProduct);
                      setSelectedBatch(batch);
                      setCustomPrice(item.price);
                      setCustomQty(item.qty);
                      if (item.is_tempory_item)
                        setCustomCost(item.cost_price || 0);
                      setDescription(item.description || "");
                      setCustomDiscount(item.discount_input ?? 0);
                      if (item.discount_mode) {
                        setDiscountMode(item.discount_mode);
                      }
                      removeFromCart(item.id, item.batch_no);
                      setFocusedCartItemIndex(null);
                    }}
                    className={`flex items-center gap-3.5 p-3 rounded-xl transition-all border cursor-pointer ${
                      isHighlighted
                        ? "bg-indigo-50/80 border-indigo-300 ring-2 ring-indigo-500/10 shadow-sm cart-item-highlighted"
                        : "bg-gray-50/50 border-gray-100 hover:border-indigo-200 hover:bg-indigo-50/30"
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-gray-900 text-xs truncate">
                        {item.name}
                      </h4>
                      <p className="text-[10px] text-gray-400 mt-0.5">
                        LKR{" "}
                        {item.price.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                        })}{" "}
                        x {item.qty}
                        {item.cost_price !== undefined &&
                          item.cost_price !== null &&
                          ` (Cost: LKR ${Number(item.cost_price).toLocaleString(undefined, { minimumFractionDigits: 2 })})`}
                      </p>
                      {item.discount_amount != null &&
                        item.discount_amount > 0 && (
                          <span className="text-[10px] text-rose-500 font-semibold">
                            Disc: -LKR{" "}
                            {item.discount_amount.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                            })}
                          </span>
                        )}
                      <span className="inline-block bg-gray-100 text-gray-500 font-bold px-1.5 py-0.5 rounded text-[9px] mt-1 uppercase select-none">
                        Batch: {item.batch_no}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-bold text-gray-900 text-xs">
                        LKR{" "}
                        {(item.price * item.qty).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                        })}
                      </span>
                      {!item.rawProduct?.is_repair_item &&
                      isAllowed(PosAction.VoidTransaction) ? (
                        <Button
                          type="text"
                          size="small"
                          danger
                          onClick={() => removeFromCart(item.id, item.batch_no)}
                          className="hover:bg-red-50 rounded-lg"
                        >
                          ✕
                        </Button>
                      ) : (
                        <div className="w-6" />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Cart totals & Action */}
        <div className="border-t border-gray-100 pt-5 mt-5 flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between text-xs text-gray-500 font-medium">
              <span>Subtotal</span>
              <span>LKR {cartTotal.toLocaleString()}.00</span>
            </div>
            {exchangeDiscount > 0 && (
              <div className="flex items-center justify-between text-xs text-green-600 font-semibold">
                <span>Exchange Offset</span>
                <span>- LKR {exchangeDiscount.toLocaleString()}.00</span>
              </div>
            )}
            <div className="flex items-center justify-between text-xs text-gray-500 font-medium">
              <span>Tax (0.00%)</span>
              <span>LKR 0.00</span>
            </div>
            <div className="flex items-center justify-between border-t border-gray-100 pt-3 mt-1">
              <span className="font-bold text-gray-900 text-sm">
                Grand Total
              </span>
              <span className="font-black text-indigo-600 text-base">
                LKR {Math.max(0, cartTotal - exchangeDiscount).toLocaleString()}
                .00
              </span>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <Button
              type="primary"
              size="large"
              disabled={cart.length === 0}
              onClick={onOpenToPay}
              icon={<Banknote className="w-4 h-4" />}
              className="w-full h-12 mt-2 border-none rounded-xl font-semibold bg-green-600 hover:bg-green-700 shadow-sm shadow-green-600/20"
            >
              Pay
            </Button>
            <Button
              type="primary"
              size="large"
              onClick={onOpenReturn}
              icon={<Undo2 className="w-4 h-4" />}
              className="w-full h-12 mt-2 border-none rounded-xl font-semibold bg-blue-600 hover:bg-blue-700 shadow-sm shadow-blue-600/20"
            >
              Return
            </Button>

            <Button
              type="link"
              size="large"
              onClick={onOpenExpenses}
              icon={<ReceiptText className="w-4 h-4" />}
              className="w-full h-12 mt-2 border-none rounded-xl font-semibold bg-orange-500 hover:bg-orange-600 text-white shadow-sm shadow-orange-500/20"
            >
              Expenses
            </Button>
          </div>
        </div>
      </section>
    </>
  );
};
